import { useState, type FormEvent } from 'react';
import { groupInput, type GroupInput } from '@redirector/shared';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { Field } from '../ui/Field.js';
import {
  issuesToFieldErrors,
  submitErrorToFieldErrors,
  type FieldErrors,
} from '../ui/formErrors.js';

export type GroupFormProps = {
  initial?: { name: string; notes: string };
  submitLabel: string;
  onSubmit: (input: GroupInput) => Promise<void>;
  onCancel: () => void;
};

const INPUT =
  'w-full rounded-(--radius-md) border border-rule-strong bg-surface-raised px-(--space-3) py-(--space-2) text-ink';

export function GroupForm({ initial, submitLabel, onSubmit, onCancel }: GroupFormProps) {
  const [fields, setFields] = useState({ name: initial?.name ?? '', notes: initial?.notes ?? '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const parsed = groupInput.safeParse(fields);
    if (!parsed.success) {
      setErrors(issuesToFieldErrors(parsed.error.issues));
      return;
    }

    setErrors({});
    setBusy(true);
    try {
      await onSubmit(parsed.data);
    } catch (error) {
      setErrors(submitErrorToFieldErrors(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-(--space-4)" noValidate>
      {errors.root !== undefined && <Callout tone="danger">{errors.root}</Callout>}

      <Field label="Name" error={errors.name} hint="The client this group belongs to.">
        {({ id, describedBy, invalid }) => (
          <input
            id={id}
            autoFocus
            className={INPUT}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            value={fields.name}
            onChange={(event) => setFields((prev) => ({ ...prev, name: event.target.value }))}
          />
        )}
      </Field>

      <Field label="Notes" error={errors.notes} hint="Optional. Context for whoever comes next.">
        {({ id, describedBy }) => (
          <textarea
            id={id}
            rows={3}
            className={INPUT}
            aria-describedby={describedBy}
            value={fields.notes}
            onChange={(event) => setFields((prev) => ({ ...prev, notes: event.target.value }))}
          />
        )}
      </Field>

      <div className="flex justify-end gap-(--space-2)">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}
